/* eslint-disable no-underscore-dangle */
import { Component, AfterViewInit, OnInit, OnChanges, Output, EventEmitter, Input,
    ViewChildren, ViewContainerRef, QueryList } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';

import { HisValueGroup, GenericValue, HisDynamicFieldConfig, Language,
    HisBaseGlobalsService, HisGlobalDataService } from 'hisng';

import { DYNAMIC_FIELD_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';

import { DATA_SERVICE_KEY_PUBLICATION, DATA_SERVICE_KEY_CHILD_PUBLICATION } from 'app/fs/res/publication/model/constants';
import { PublicationAuthService } from 'app/fs/res/publication/service/publication-auth.service';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { PublicationType } from 'app/fs/res/publication/model/publication-type';
import { Publication } from 'app/fs/res/publication/model/publication';
import { Duplicate } from 'app/fs/res/common/model/duplicate';
import { DynamicContentUtils } from 'app/fs/res/common/service/dynamic-content/dynamic-content-utils';

const TITLE_KEY = 'Publication.title';

@Component( {
    selector: 'res-publication-edit-main-data',
    templateUrl: './publication-edit-main-data.component.html',
    providers: [ProviderManager.getComponentProvider(PublicationEditMainDataComponent)],
} )
export class PublicationEditMainDataComponent implements AfterViewInit, OnInit, OnChanges {
    @Input('publication') set pub(publication: Publication) {
        this.publication = publication;
        if (publication?.changeRequest) {
            this.visibilitayDynamicFieldConfig =
                this.dynamicContentUtils.createDynamicFormFieldConfig(this.publication.changeRequest._dynamicComponent, 'visibilityValue');
        } else {
            this.visibilitayDynamicFieldConfig = null;
        }
    }
    publication: Publication;
    @Input() childPublication: Publication;
    @Input() publicationTypeChildParentMap: {[key: string]: PublicationType[]};
    @Input() changeRequestValueGroup = new HisValueGroup( 1 );
    @Output() publicationTypeChange: EventEmitter<GenericValue> = new EventEmitter<GenericValue>();
    @Output() navigationLastUrlChange = new EventEmitter<string>();
    @ViewChildren( DYNAMIC_FIELD_ANCHOR, { read: ViewContainerRef } ) fieldViewContainerList: QueryList<ViewContainerRef>;
    @ViewChildren(AutoComplete) autoCompleteViewChildren!: QueryList<AutoComplete>;

    public hasStatusAuthority = false;

    /** (potentielle) Duplikate nach Titel */
    public duplicates: Duplicate[] = null;
    @Output() duplicatesByTitleChange = new EventEmitter<Duplicate[]>();

    private hiskeyIdsForPublicationTypeBook = [1, 2, 16];

    visibilitayDynamicFieldConfig: HisDynamicFieldConfig;
    changeRequesForm: UntypedFormGroup = new UntypedFormGroup( {} );

    constructor(
        private hisBaseGlobalsService: HisBaseGlobalsService,
        private publicationAuthService: PublicationAuthService,
        private globalDataService: HisGlobalDataService,
        private publicationService: PublicationService,
        private dynamicContentUtils: DynamicContentUtils,
        private ddUtils: DataDictionaryUtils ) {
    }

    ngOnInit() {
        this.hasStatusAuthority = this.publicationAuthService.hasStatusAuthority();
    }

    ngAfterViewInit() {
        // trigger re-render count of ViewChildren dynamicFieldContainer
        this.fieldViewContainerList.changes.subscribe( (changes: any) => {
            // eslint-disable-next-line no-underscore-dangle
            if ( this.publication._dynamicComponent && this.hisBaseGlobalsService.isAnyModuleActive( 'HISINONERES' ) ) {
                // eslint-disable-next-line no-underscore-dangle
                this.publication._dynamicComponent = { ...this.publication._dynamicComponent };
            }
        } );
    }

    ngOnChanges() {
        // publication.title is not available yet in ngOnInit() because publication is loaded asyncronously via a web service
        this.checkForDuplicates();
    }

    public checkForDuplicates() {
        let title: string = this.publication.title;
        console.log('Publication duplicates check by title: title =', title);

        if ( !title || title.trim().length === 0 ) {
            console.log('Publication duplicates check by title: Nothing to check...');
            this.duplicates = null;
            this.duplicatesByTitleChange.emit(this.duplicates);
            return;
        }

        // check for duplicates by title
        let attributesMap: Record<string, string[]> = {};
        attributesMap[TITLE_KEY] = [title.trim()];

        this.publicationService.checkDuplicatesOnCreate( attributesMap ).subscribe( (dupCheckResult: Publication[]) => {
            this.duplicates = dupCheckResult.filter( dup => !this.publication.id || dup.id !== this.publication.id )
                .map( p => new Duplicate(p.id, p.title, Publication.getDescription(p)) );
            if (this.duplicates?.length > 0) {
                console.log( 'Publication duplicates check by title: Found potential duplicates:', this.duplicates );
            } else {
                console.log( 'Publication duplicates check by title: No duplicates found' );
            }
            this.duplicatesByTitleChange.emit(this.duplicates);
        });
    }

    public get isPublicationTypeBook() {
        return this.hiskeyIdsForPublicationTypeBook.some(hiskeyId => hiskeyId === this.publication.publicationType?.hiskeyId);
    }

    public getDdKey( source: string ): string {
        if (!this.publication.publicationType) {
            //#297250 default-lable fuer auswahlbox documenttype, wenn unbekannte Publikation
            return 'fs.res.publication.PublicationDocumentTypeValue.longtext';
        }
        return DataDictionaryUtils.dDKeyPublicationType( this.publication.publicationType, source );
    }

    public getIsDynamicContainerVisible( source: string ): boolean {
        return this.ddUtils.isVisible( this.getDdKey( source ) );
    }

    public onPublicationTypeChange( event: GenericValue ) {
        // just emit if publicationType really changes
        if ( event.hiskeyId !== this.publication.publicationType.hiskeyId ) {
            this.publication.publicationType = event;
            this.publicationTypeChange.emit( event );
        }
    }

    public onClear( field: string ) {
        switch ( field ) {
            case 'status': this.publication.status = null; break;
            case 'visibilityValue': this.publication.visibilityValue = null; break;
            default: console.log( 'ERROR: Unsupported field:', field );
        }
    }

    public onSelectedLanguageChange( $event: Language[] ) {
        // console.log('Obtained languages change event:', $event);
        this.publication.languages = $event;
    }

    public onBeforeNavigate() {
        this.navigationLastUrlChange.emit( window.location.href );

        if (this.publication) {
            this.globalDataService.setData(DATA_SERVICE_KEY_PUBLICATION, this.publication);
        }

        if (this.childPublication) {
            this.globalDataService.setData(DATA_SERVICE_KEY_CHILD_PUBLICATION, this.childPublication);
        }
    }

    public clearAutocompleteForDocTypesIfNecessary() {
        let autoCompleteName = this.isPublicationTypeBook ? 'docTypeBook' : 'docTypeArticle';
        if(this.autoCompleteViewChildren) {
            let suggestions = this.autoCompleteViewChildren.filter(child => autoCompleteName === child.name)[0].suggestions;
            if (suggestions.every(sugg => sugg.hiskeyId !== this.publication.documentType?.hiskeyId)) {
                this.publication.documentType = {} as any;
            }
        }
    }
}
